import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { authService } from './AuthService';

const defaultBaseURL = `${import.meta.env.VITE_API_BASE_URL}/`;

function handleError(error: any) {
  return {
    error: true,
    message:
      error?.response?.data?.error?.message || error?.response?.data?.message || error.message || 'Unknown error',
    status: error?.response?.status || 500,
    data: error?.response?.data || null,
  };
}

export function useApi(baseUrl?: string) {
  const { user, updateUser } = useContext(UserContext);
  // Store user in a ref to ensure we always get the latest value
  const userRef = useRef(user);
  // Same treatment for updateUser: UserContext rebuilds it every render, and
  // reading it through a ref is what lets the returned client below stay
  // referentially stable without ever capturing a stale closure.
  const updateUserRef = useRef(updateUser);

  // Keep the ref synchronized with the user context
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    updateUserRef.current = updateUser;
  }, [updateUser]);

  // Memoised so the client keeps one identity for the life of the component.
  // It used to be rebuilt every render, which made `api` unusable as a hook
  // dependency: any effect listing it re-ran on every render, and an effect
  // that sets state (a fetched array, say) then re-rendered — an unbounded
  // request loop that ran until the API rate-limited the user. Everything
  // below reads live values through refs, so nothing goes stale.
  return useMemo(() => {
    const makeClient = (tokenOverride?: string) => {
      const token = tokenOverride ?? userRef.current?.idToken;
      return axios.create({
        baseURL: baseUrl || defaultBaseURL,
        timeout: 120000, // 2 minutes (120 seconds * 1000ms)
        headers: {
          Authorization: token ? `${token}` : '',
          'X-Page-Url': typeof window !== 'undefined' ? window.location.href : '',
        },
      });
    };

    const makeRequestWithRetry = async <T = any,>(
      requestFn: (tokenOverride?: string) => Promise<T>,
      maxRetries: number = 1,
      tokenOverride?: string,
    ): Promise<T> => {
      try {
        return await requestFn(tokenOverride);
      } catch (error: any) {
        const status = error?.response?.status;

        // Only retry on 401 errors and if we have retries left
        // Skip retry if userRef.current is not set yet (still initializing)
        if (status === 401 && maxRetries > 0) {
          // Don't retry if we don't have a user yet (context still initializing)
          if (!userRef.current) {
            throw error;
          }

          try {
            // Refresh the session to get new tokens
            const session = await authService.refreshSession();
            const newIdToken = session.getIdToken().getJwtToken();

            // Update the user context with the new token
            await updateUserRef.current((prev) => {
              // Safety check: if prev is null/undefined, don't update
              if (!prev) {
                return prev;
              }
              return { ...prev, idToken: newIdToken };
            });

            // Retry the request with the freshly refreshed token. We pass it
            // through explicitly because userRef syncs via useEffect on the
            // next render, so it would still hold the old token here.
            return await makeRequestWithRetry(requestFn, maxRetries - 1, newIdToken);
          } catch {
            // If refresh fails, return the original error
            throw error;
          }
        }

        // For non-401 errors or when retries are exhausted, throw the error
        throw error;
      }
    };

    return {
      get: async <T = any,>(url: string, config?: AxiosRequestConfig) => {
        try {
          const res = await makeRequestWithRetry((token) => makeClient(token).get<T>(url, config));
          return res.data;
        } catch (error) {
          return handleError(error);
        }
      },
      post: async <T = any,>(url: string, data?: any, config?: AxiosRequestConfig) => {
        try {
          const res = await makeRequestWithRetry((token) => makeClient(token).post<T>(url, data, config));
          return res.data;
        } catch (error) {
          return handleError(error);
        }
      },
      put: async <T = any,>(url: string, data?: any, config?: AxiosRequestConfig) => {
        try {
          const res = await makeRequestWithRetry((token) => makeClient(token).put<T>(url, data, config));
          return res.data;
        } catch (error) {
          return handleError(error);
        }
      },
      patch: async <T = any,>(url: string, data?: any, config?: AxiosRequestConfig) => {
        try {
          const res = await makeRequestWithRetry((token) => makeClient(token).patch<T>(url, data, config));
          return res.data;
        } catch (error) {
          return handleError(error);
        }
      },
      delete: async <T = any,>(url: string, config?: AxiosRequestConfig) => {
        try {
          const res = await makeRequestWithRetry((token) => makeClient(token).delete<T>(url, config));
          return res.data;
        } catch (error) {
          return handleError(error);
        }
      },
    };
  }, [baseUrl]);
}
